import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, BookOpen, AlertCircle } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [verifying, setVerifying] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [userName, setUserName] = useState('');

  useEffect(() => {
    const verifyPayment = async () => {
      const sessionId = searchParams.get('session_id');
      if (!sessionId) {
        setError('Sesión inválida');
        setVerifying(false);
        return;
      }

      const token = localStorage.getItem('reader_token');
      if (!token) {
        setError('Tu sesión expiró. Por favor iniciá sesión nuevamente para verificar tu compra.');
        setVerifying(false);
        return;
      }

      try {
        const response = await axios.post(
          `${API}/reader/verify-payment`,
          { session_id: sessionId },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data.success) {
          setSuccess(true);
          // Update local user data
          const savedUser = localStorage.getItem('reader_user');
          if (savedUser) {
            const user = JSON.parse(savedUser);
            user.hasPaid = true;
            localStorage.setItem('reader_user', JSON.stringify(user));
            setUserName(user.name?.split(' ')[0] || '');
          }
          toast.success('🎉 ¡Compra verificada exitosamente!');
        } else {
          setError('No se pudo verificar el pago. Contactanos si el problema persiste.');
        }
      } catch (error) {
        console.error('Error verifying payment:', error);
        const message = error.response?.data?.detail || 'Error al verificar el pago';
        setError(message);
      } finally {
        setVerifying(false);
      }
    };

    verifyPayment();
  }, [searchParams]);

  if (verifying) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-gray-800/50 border-gray-700">
          <CardContent className="p-12 text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-amber-500 border-t-transparent mx-auto mb-4"></div>
            <p className="text-lg text-gray-300">Verificando tu pago...</p>
            <p className="text-sm text-gray-500 mt-2">Esto solo toma unos segundos</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-gray-800/50 border-gray-700">
          <CardContent className="p-12 text-center">
            <AlertCircle className="text-red-500 mx-auto mb-4" size={64} />
            <h2 className="text-xl font-bold text-white mb-4">Error de verificación</h2>
            <p className="text-gray-300 mb-6">{error}</p>
            <div className="space-y-3">
              <button
                onClick={() => navigate('/reader/login')}
                className="w-full px-6 py-3 bg-amber-500 hover:bg-amber-600 text-white rounded-full font-semibold transition-colors"
              >
                Iniciar Sesión
              </button>
              <button
                onClick={() => navigate('/reader/biblioteca')}
                className="w-full px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-full font-semibold transition-colors"
              >
                Ir a Mi Biblioteca
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!success) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center p-4">
      <Card className="max-w-2xl w-full bg-gray-800/50 border-gray-700 shadow-2xl">
        <CardContent className="p-8 md:p-12 text-center">
          {/* Success Animation */}
          <div className="inline-block bg-green-500/20 p-6 rounded-full mb-6 animate-pulse">
            <CheckCircle size={64} className="text-green-500" />
          </div>
          
          <h1 className="text-3xl md:text-4xl font-serif font-bold text-white mb-4">
            ¡Gracias por tu compra{userName ? `, ${userName}` : ''}! 🎉
          </h1>
          
          <p className="text-lg md:text-xl text-gray-300 mb-8">
            Ya tenés acceso completo a <strong className="text-amber-400">"Cábala para principiantes"</strong>. 
            Tu viaje por el Árbol de la Vida continúa sin límites.
          </p>

          {/* Benefits */}
          <div className="bg-gradient-to-r from-green-900/30 to-emerald-900/30 p-6 rounded-xl mb-8 border border-green-700/50">
            <h3 className="text-lg font-bold text-white mb-4">✨ Lo que ahora tenés:</h3>
            <ul className="text-left space-y-3 text-gray-300">
              <li className="flex items-center gap-3">
                <span className="text-green-500 text-xl">✓</span>
                <span>Acceso completo a las <strong>176 páginas</strong> del libro</span>
              </li>
              <li className="flex items-center gap-3">
                <span className="text-green-500 text-xl">✓</span>
                <span><strong>Descarga del PDF</strong> para leer offline</span>
              </li>
              <li className="flex items-center gap-3">
                <span className="text-green-500 text-xl">✓</span>
                <span>Actualizaciones futuras <strong>sin costo adicional</strong></span>
              </li>
              <li className="flex items-center gap-3">
                <span className="text-green-500 text-xl">✓</span>
                <span>Soporte directo por <strong>WhatsApp</strong></span>
              </li>
            </ul>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/reader')}
              className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white px-8 py-4 rounded-full text-lg font-bold transition-all duration-300 transform hover:scale-105 shadow-lg shadow-amber-500/30"
            >
              <BookOpen size={24} />
              <span>Continuar Leyendo</span>
            </button>
            
            <button
              onClick={() => navigate('/reader/biblioteca')}
              className="inline-flex items-center justify-center gap-2 bg-gray-700 hover:bg-gray-600 text-white px-8 py-4 rounded-full text-lg font-semibold transition-all"
            >
              Ir a Mi Biblioteca
            </button>
          </div>

          <p className="text-sm text-gray-500 mt-8">
            📧 Recibirás un email de confirmación con los detalles de tu compra.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};